import { NavigationService } from '@core/services/navigation.service';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { FuseNavigationItem } from '@fuse/components/navigation';

/**
 * Exactly the values `app.routes.ts` puts in `data.state`. 'paywall' used to be
 * here and 'auth'/'blocked' were not, which type-checked only because route
 * data is untyped: the two real states flowed in as strings while the declared
 * one had no route and an empty navigation array.
 */
export type NavigationStateKeys = 'app' | 'quiz' | 'auth' | 'blocked';

export const getNavigationConfig = (
  navService: NavigationService,
  mixpanel: MixpanelService
): Partial<Record<NavigationStateKeys, FuseNavigationItem[]>> => {
  const app: FuseNavigationItem[] = [
    {
      id: 'accounts',
      title: 'Accounts',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:credit-card',
      link: '/accounts',
      children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
    },
    {
      id: 'insights',
      title: 'Insights',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:chart-pie',
      link: '/insights',
      children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:cube',
      link: '/dashboard',
      children: [], // This will be filled from defaultNavigation so we don't have to manage multiple sets of the same navigation
    },
    {
      id: 'transactions',
      title: 'Transactions',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:arrows-up-down',
      link: '/transactions',
      children: [],
    },
    {
      id: 'plan',
      title: 'Plan',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:queue-list',
      link: '/plan',
      children: [],
    },
    {
      id: 'debt-plan',
      title: 'Debt payoff plan',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:shopping-bag',
      link: '/debt-plan',
      children: [],
    },
    {
      id: 'goals',
      title: 'Goals',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:trophy',
      link: '/goals',
      children: [],
    },
    {
      id: 'help-support',
      title: 'Help & Support',
      tooltip: '',
      type: 'collapsable',
      icon: 'heroicons_outline:chat-bubble-oval-left-ellipsis',
      function: (_item) => {
        mixpanel.track('Settings - Help & Support');
      },
      children: [
        {
          id: 'help',
          title: 'Help Center',
          tooltip: '',
          type: 'basic',
          externalLink: true,
          link: navService.supportLinks.helpCenter,
          target: '_blank',
          function: (_item) => {
            mixpanel.track('Help Center - button click');
          },
        },
        {
          id: 'contact',
          title: 'Contact us',
          tooltip: '',
          type: 'basic',
          link: '/support',
          function: (_item) => {
            mixpanel.track('Contact us - button click');
          },
        },
        {
          id: 'reddit',
          title: 'Reddit community',
          tooltip: '',
          type: 'basic',
          externalLink: true,
          link: navService.supportLinks.redditCommunity,
          target: '_blank',
          function: (_item) => {
            mixpanel.track('Join Reddit community - button click');
          },
        },
      ],
    },
  ];
  const quiz: FuseNavigationItem[] = [
    {
      id: 'support',
      title: 'Help & Support',
      tooltip: '',
      type: 'collapsable',
      icon: 'heroicons_outline:chat-bubble-oval-left-ellipsis',
      children: [
        {
          id: 'help',
          title: 'Help Center',
          tooltip: '',
          type: 'basic',
          externalLink: true,
          link: navService.supportLinks.helpCenter,
          target: '_blank',
          function: (_item) => {
            mixpanel.track('Help Center - button click');
          },
        },
        {
          id: 'contact',
          title: 'Contact us',
          tooltip: '',
          type: 'basic',
          link: '/start/support',
          function: (_item) => {
            mixpanel.track('Contact us - button click');
          },
        },
        {
          id: 'reddit',
          title: 'Reddit community',
          tooltip: '',
          type: 'basic',
          externalLink: true,
          link: navService.supportLinks.redditCommunity,
          target: '_blank',
          function: (_item) => {
            mixpanel.track('Join Reddit community - button click');
          },
        },
      ],
    },
    {
      id: 'logout',
      title: 'Logout',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:arrow-right-on-rectangle',
      function: (item) => {
        navService.navItemClick(item);
      },
    },
    {
      id: 'delete',
      title: 'Delete profile',
      tooltip: '',
      type: 'basic',
      icon: 'heroicons_outline:trash',
      classes: {
        title: 'text-warn',
        icon: 'text-warn',
      },
      function: (item) => {
        navService.navItemClick(item);
      },
    },
  ];
  // 'auth' and 'blocked' deliberately have no entry: those screens show no
  // side navigation, and setNavigation() simply leaves the last one in place.
  return { app, quiz };
};
